import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    Button, Row, Col, Card, CardBody, Input, Badge,
    Form, Label, Table
} from "reactstrap";
import {
    FiDollarSign, FiUsers, FiActivity, FiRefreshCcw,
    FiCalendar, FiPieChart, FiArrowRight,
    FiTrendingDown
} from "react-icons/fi";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Cell, Legend, LineChart, Line, PieChart, Pie
} from "recharts";
import Select from "react-select"; // Addon machan
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";

// MUI Date Picker addon logic from Dashboard ref
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const SalaryAnalyticsReport = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [rawResponse, setRawResponse] = useState(null);

    // Filters state addon with Dashboard logic
    const [filters, setFilters] = useState({
        year: new Date().getFullYear(),
        staff_id: "",
        filter_period: { value: "year", label: "Full Year" }, // React-select object
        from_date: dayjs().startOf('year'), // MUI dayjs object
        to_date: dayjs()
    });

    const filterOptions = [
        { value: "today", label: "Today" },
        { value: "week", label: "Last Week" },
        { value: "month", label: "Last Month" },
        { value: "3months", label: "Last 3 Months" },
        { value: "year", label: "Full Year" },
        { value: "custom", label: "Custom Range" }
    ];

    const COLORS = ['#0d6efd', '#6610f2', '#6f42c1', '#d63384', '#dc3545', '#fd7e14', '#ffc107', '#198754'];

    const loadSalaryReport = async (currentFilters = filters) => {
        dispatch(loadingTrue());
        try {
            const queryParams = new URLSearchParams({
                year: currentFilters.year,
                staff_id: currentFilters.staff_id,
                filter: currentFilters.filter_period.value,
                from_date: currentFilters.from_date ? currentFilters.from_date.format('YYYY-MM-DD') : "",
                to_date: currentFilters.to_date ? currentFilters.to_date.format('YYYY-MM-DD') : ""
            }).toString();

            const res = await Handler({ method: "GET", url: `/report/salary_report.php?${queryParams}` });
            if (res?.success) {
                setRawResponse(res?.data?.analytics);
            }
        } catch (err) {
            toast.error("Failed to sync Salary Report");
        } finally {
            dispatch(loadingFalse());
        }
    };

    useEffect(() => {
        if (filters.filter_period.value !== 'custom') {
            loadSalaryReport();
        }
    }, [filters.year, filters.staff_id]);

    const handlePeriodChange = (selectedOption) => {
        const type = selectedOption.value;
        let from = dayjs();
        let to = dayjs();

        if (type === 'week') from = dayjs().subtract(7, 'day');
        else if (type === 'month') from = dayjs().subtract(1, 'month');
        else if (type === '3months') from = dayjs().subtract(3, 'month');
        else if (type === 'year') from = dayjs().startOf('year');

        const newFilters = { ...filters, filter_period: selectedOption, from_date: from, to_date: to };
        setFilters(newFilters);

        if (type !== 'custom') {
            loadSalaryReport(newFilters);
        }
    };

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            backgroundColor: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: "10px",
            minHeight: "45px",
            boxShadow: "none",
            '&:hover': { border: '1px solid #f16436' }
        })
    };

    // 🔥 Monthly Trend Transformation (Maintained logic)
    const monthlyTrend = useMemo(() => {
        const labels = rawResponse?.monthly_chart?.labels || [];
        const datasets = rawResponse?.monthly_chart?.datasets || [];

        return labels.map((monthName, i) => {
            return {
                month: monthName,
                total: datasets[0]?.data[i] || 0,
                paid: datasets[1]?.data[i] || 0,
                pending: datasets[2]?.data[i] || 0,
            };
        });
    }, [rawResponse]);

    // 🔥 Staff Distribution Transformation (Maintained logic)
    const staffDist = useMemo(() => {
        const labels = rawResponse?.staff_chart?.labels || [];
        const values = rawResponse?.staff_chart?.data || [];
        return labels.map((label, i) => ({ name: label, value: values[i] }));
    }, [rawResponse]);

    const summary = rawResponse?.summary || {};

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-container p-3 bg-light min-vh-100">
                {/* Header addon from ref */}
                <div className="d-md-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h3 className="fw-bold m-0 text-dark">Salary & Payroll Analytics</h3>
                        <p className="text-muted small m-0">Payroll performance tracking.</p>
                    </div>
                    <div className="d-flex gap-2 mt-3 mt-md-0 align-items-center">
                        <div style={{ width: '180px' }}>
                            <Select
                                options={filterOptions}
                                value={filters.filter_period}
                                onChange={handlePeriodChange}
                                styles={customSelectStyles}
                            />
                        </div>
                        <Button color="white" className="shadow-sm border rounded-pill px-3 py-2" onClick={() => loadSalaryReport()}>
                            <FiRefreshCcw />
                        </Button>
                    </div>
                </div>

                {/* MUI Custom Range Filter Addon */}
                {filters.filter_period.value === 'custom' && (
                    <Card className="border-0 shadow-sm rounded-4 mb-4">
                        <CardBody className="p-4">
                            <Row className="g-3 align-items-end">
                                <Col md={4}>
                                    <Label className="x-small fw-bold text-muted d-block mb-2 text-uppercase">From Date</Label>
                                    <DatePicker
                                        disableFuture
                                        format="DD/MM/YYYY"
                                        value={filters.from_date}
                                        maxDate={dayjs()}
                                        onChange={(val) => setFilters({ ...filters, from_date: val })}
                                        sx={{ width: '100%', '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: '#fff' } }}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label className="x-small fw-bold text-muted d-block mb-2 text-uppercase">To Date</Label>
                                    <DatePicker
                                        disableFuture
                                        format="DD/MM/YYYY"
                                        value={filters.to_date}
                                        minDate={filters.from_date}
                                        maxDate={dayjs()}
                                        onChange={(val) => setFilters({ ...filters, to_date: val })}
                                        sx={{ width: '100%', '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: '#fff' } }}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Button color="primary" block className="fw-bold rounded-3 shadow-sm" style={{ height: '54px' }} onClick={() => loadSalaryReport()}>
                                        Generate Statement
                                    </Button>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                )}

                {/* Summary Cards (Maintained) */}
                <Row className="g-3 mb-4">
                    <Col lg={4}>
                        <StatCard title="Annual Payout" value={`₹${summary?.total_salary?.toLocaleString() || 0}`} icon={<FiDollarSign />} color="#0d6efd" bg="#0d6efd15" />
                    </Col>
                    <Col lg={4}>
                        <StatCard title="Total Disbursed" value={`₹${summary?.total_paid?.toLocaleString() || 0}`} icon={<FiActivity />} color="#198754" bg="#19875415" />
                    </Col>
                    <Col lg={4}>
                        <StatCard title="Pending Payouts" value={`₹${summary?.total_pending?.toLocaleString() || 0}`} icon={<FiTrendingDown />} color="#dc3545" bg="#dc354515" />
                    </Col>
                </Row>

                {/* Charts Row (Maintained) */}
                <Row className="g-4">
                    <Col lg={8}>
                        <Card className="border-0 shadow-sm rounded-4 mb-4 overflow-hidden h-100">
                            <CardBody className="p-4">
                                <h6 className="fw-bold mb-4 text-muted small text-uppercase ls-1">Monthly Payout Realization</h6>
                                <div style={{ width: '100%', height: 350 }}>
                                    <ResponsiveContainer>
                                        <BarChart data={monthlyTrend}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                            <XAxis dataKey="month" axisLine={false} tickLine={false} fontSize={11} />
                                            <YAxis axisLine={false} tickLine={false} fontSize={11} />
                                            <Tooltip cursor={{ fill: '#f8f9fa' }} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                            <Legend verticalAlign="top" align="right" height={36} iconType="circle" />
                                            <Bar name="Paid" dataKey="paid" fill="#198754" radius={[4, 4, 0, 0]} barSize={25} />
                                            <Bar name="Pending" dataKey="pending" fill="#dc3545" radius={[4, 4, 0, 0]} barSize={25} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>

                    <Col lg={4}>
                        <Card className="border-0 shadow-sm rounded-4 mb-4 h-100">
                            <CardBody className="p-4 text-center">
                                <h6 className="fw-bold mb-4 text-muted text-start small text-uppercase ls-1">Staff Share (%)</h6>
                                <div style={{ width: '100%', height: 300 }}>
                                    <ResponsiveContainer>
                                        <PieChart>
                                            <Pie
                                                data={staffDist}
                                                innerRadius={70}
                                                outerRadius={100}
                                                paddingAngle={5}
                                                dataKey="value"
                                            >
                                                {staffDist.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className="mt-2">
                                    <Badge color="light" className="text-dark border rounded-pill py-2 px-3">
                                        <FiUsers className="me-2" /> Contributor Breakdown
                                    </Badge>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Detailed Statement Table (Maintained) */}
                <Card className="border-0 shadow-sm rounded-4 overflow-hidden mt-4">
                    <div className="bg-white p-3 border-bottom d-flex align-items-center justify-content-between">
                        <h6 className="m-0 fw-bold">Annual Financial Recap</h6>
                        <Badge color="soft-primary" className="text-primary">Sync Status: Active</Badge>
                    </div>
                    <Table responsive hover className="mb-0 align-middle">
                        <thead className="bg-light">
                            <tr className="text-muted small text-uppercase ls-1">
                                <th className="ps-4 py-3">Head</th>
                                <th className="text-end pe-4">Valuation (INR)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="ps-4">Total Net Salary (Employee Dues)</td>
                                <td className="text-end pe-4 fw-bold">₹{summary?.total_salary?.toLocaleString()}</td>
                            </tr>
                            <tr>
                                <td className="ps-4 text-success"><FiArrowRight className="me-2" /> Amount Settled (Paid)</td>
                                <td className="text-end pe-4 text-success fw-bold">₹{summary?.total_paid?.toLocaleString()}</td>
                            </tr>
                            <tr className="border-top-2">
                                <td className="ps-4 text-danger fw-bold fs-6">Outstanding Balance (Pending)</td>
                                <td className="text-end pe-4 text-danger fw-bold fs-5">₹{summary?.total_pending?.toLocaleString()}</td>
                            </tr>
                        </tbody>
                    </Table>
                </Card>
            </div>
        </LocalizationProvider>
    );
};

const StatCard = ({ title, value, icon, color, bg }) => (
    <Card className="border-0 shadow-sm rounded-4 h-100 bg-white">
        <CardBody className="d-flex align-items-center gap-3 p-4">
            <div style={{
                width: '50px', height: '50px', borderRadius: '15px',
                backgroundColor: bg, color: color,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px'
            }}>
                {icon}
            </div>
            <div>
                <p className="m-0 text-muted x-small fw-bold text-uppercase ls-1">{title}</p>
                <h4 className="m-0 fw-bold" style={{ color: '#2c3e50' }}>{value}</h4>
            </div>
        </CardBody>
    </Card>
);

export default SalaryAnalyticsReport;